/**
 * Videocall UI Helpers
 *
 * Modal UI utilities: loading animations, info messages, error display,
 * background video, and voice matching.
 */
const $ = jQuery;
const VIDEO_DURATION_SAMPLE_SIZE = 8;
const VIDEO_DURATION_PROBE_TIMEOUT = 1800;

function loadVideoDuration(url) {
    return new Promise(resolve => {
        const probe = document.createElement('video');
        let timeoutId = null;
        let settled = false;

        const finish = duration => {
            if (settled) return;
            settled = true;
            if (timeoutId) clearTimeout(timeoutId);
            probe.removeAttribute('src');
            probe.load();
            resolve(Number.isFinite(duration) ? duration : 0);
        };

        probe.preload = 'metadata';
        probe.muted = true;
        probe.addEventListener('loadedmetadata', () => finish(probe.duration), {once: true});
        probe.addEventListener('error', () => finish(0), {once: true});
        timeoutId = setTimeout(() => finish(0), VIDEO_DURATION_PROBE_TIMEOUT);
        probe.src = url;
        probe.load();
    });
}

export async function prioritizeLongerVideos(playlist, durationLoader = loadVideoDuration, sampleSize = VIDEO_DURATION_SAMPLE_SIZE) {
    const candidates = playlist.slice(0, sampleSize);
    const rankedCandidates = await Promise.all(candidates.map(async (url, index) => {
        try {
            return {url, index, duration: await durationLoader(url)};
        } catch (error) {
            return {url, index, duration: 0};
        }
    }));

    rankedCandidates.sort((first, second) => {
        const durationDifference = second.duration - first.duration;
        return durationDifference || first.index - second.index;
    });

    return rankedCandidates.map(video => video.url).concat(playlist.slice(sampleSize));
}

/**
 * Set up background video with random start time and playlist rotation.
 */
export function setupBackgroundVideo(modal, options = {}) {
    // The Live Call modal can also contain the caller's selfie preview and a
    // provider-owned avatar stream. Background media is the only video with a
    // playlist contract, so select it by role rather than DOM position.
    const bgVideo = modal.find('.video-wrapper video[data-video-playlist]')[0];
    if (!bgVideo) return;

    const VIDEO_FADE_DURATION = 520;
    const source = bgVideo.querySelector('source');
    let playlist = [];

    try {
        const parsedPlaylist = JSON.parse(bgVideo.getAttribute('data-video-playlist') || '[]');
        playlist = Array.isArray(parsedPlaylist)
            ? [...new Set(parsedPlaylist.map(url => String(url || '').trim()).filter(Boolean))]
            : [];
    } catch (error) {
        console.warn("Could not parse background video playlist:", error);
    }

    const initialUrl = source ? source.getAttribute('src') : bgVideo.currentSrc;
    if (!playlist.length && initialUrl) playlist = [initialUrl];

    let currentIndex = Math.max(0, playlist.indexOf(initialUrl));
    let failedVideos = 0;
    let isSwitchingVideo = false;
    let fadeTimeout = null;

    const switchToNextVideo = () => {
        if (playlist.length <= 1 || !source) return false;

        currentIndex = (currentIndex + 1) % playlist.length;
        source.setAttribute('src', playlist[currentIndex]);
        bgVideo.load();
        return true;
    };

    const advanceVideo = (skipFade = false) => {
        if (playlist.length <= 1 || !source || isSwitchingVideo) return;

        isSwitchingVideo = true;
        bgVideo.classList.add('is-video-transitioning');

        if (skipFade) {
            switchToNextVideo();
            return;
        }

        fadeTimeout = setTimeout(() => {
            fadeTimeout = null;
            switchToNextVideo();
        }, VIDEO_FADE_DURATION);
    };

    bgVideo.loop = playlist.length <= 1;

    bgVideo.addEventListener('loadedmetadata', function () {
        try {
            failedVideos = 0;
            const randomTime = Math.random() * bgVideo.duration;
            bgVideo.currentTime = randomTime;
            bgVideo.play().catch(error => {
                console.warn("Could not autoplay background video:", error);
            });
            bgVideo.playbackRate = 0.5;
        } catch (error) {
            console.warn("Error setting up background video:", error);
        }
    });

    bgVideo.addEventListener('ended', () => advanceVideo());

    bgVideo.addEventListener('canplay', function () {
        if (!isSwitchingVideo) return;

        bgVideo.classList.remove('is-video-transitioning');
        isSwitchingVideo = false;
    });

    bgVideo.addEventListener('error', function (e) {
        console.error("Background video error:", e);

        if (playlist.length > 1 && failedVideos < playlist.length - 1) {
            failedVideos += 1;
            if (fadeTimeout) {
                clearTimeout(fadeTimeout);
                fadeTimeout = null;
            }
            isSwitchingVideo = false;
            advanceVideo(true);
        }
    });

    if (playlist.length > 1 && source) {
        isSwitchingVideo = true;
        bgVideo.classList.add('is-video-transitioning');

        return prioritizeLongerVideos(
            playlist,
            options.loadVideoDuration || loadVideoDuration,
            options.durationSampleSize || VIDEO_DURATION_SAMPLE_SIZE
        ).then(prioritizedPlaylist => {
            playlist = prioritizedPlaylist;
            currentIndex = 0;
            bgVideo.setAttribute('data-video-playlist', JSON.stringify(playlist));
            source.setAttribute('src', playlist[currentIndex]);
            bgVideo.load();
            return playlist;
        }).catch(error => {
            console.warn("Could not prioritize background videos by duration:", error);
            currentIndex = Math.max(0, playlist.indexOf(initialUrl));
            bgVideo.load();
            return playlist;
        });
    }

    bgVideo.load();
    return Promise.resolve(playlist);
}

/**
 * Start the "Loading..." dots animation in the videocall modal.
 *
 * @param {jQuery} modal
 * @param {jQuery} infoTextContainer   The .e-text element
 * @param {object} dotsState           Mutable { interval: number|null }
 * @param {function} isModalOpen       Returns whether modal is still open
 */
export function startLoadingAnimation(modal, infoTextContainer, dotsState, isModalOpen) {
    infoTextContainer.addClass('is-active');
    let dots = "";
    dotsState.interval = setInterval(() => {
        if (!isModalOpen()) {
            clearInterval(dotsState.interval);
            return;
        }
        dots = dots.length < 3 ? dots + "." : "";
        modal.find('.e-text[data-type="info"]').text("Loading" + dots);
    }, 500);
}

/**
 * Stop the loading animation and hide the info message.
 *
 * @param {jQuery} infoTextContainer
 * @param {object} dotsState           Mutable { interval: number|null }
 * @param {function} hideInfoMessage
 * @param {function} debugLog
 */
export function stopLoadingAnimation(infoTextContainer, dotsState, hideInfoMessage, debugLog) {
    if (dotsState.interval) {
        clearInterval(dotsState.interval);
        dotsState.interval = null;
    }
    if (infoTextContainer) {
        hideInfoMessage();
    }
    debugLog("✨ Loading animation cleared");
}

/**
 * Hide the info text and reset to "Loading".
 */
export function hideInfoMessageFn(infoTextContainer) {
    infoTextContainer.text("Loading");
    infoTextContainer.removeClass('is-active');
}

/**
 * Show a text message in the info area.
 */
export function showInfoMessageFn(infoTextContainer, text) {
    infoTextContainer.text(text);
    infoTextContainer.addClass('is-active');
}

/**
 * Show a safe support message with an optional mail link.
 */
export function showSupportErrorMessage(infoTextContainer, supportEmail) {
    const message = document.createTextNode('We couldn’t start the call. Please contact our ');
    const ending = document.createTextNode('.');
    infoTextContainer.empty();
    infoTextContainer[0].appendChild(message);

    if (supportEmail) {
        const link = document.createElement('a');
        link.href = `mailto:${supportEmail}`;
        link.textContent = 'support';
        infoTextContainer[0].appendChild(link);
    } else {
        infoTextContainer[0].appendChild(document.createTextNode('support'));
    }

    infoTextContainer[0].appendChild(ending);
    infoTextContainer.addClass('is-active');
}

/**
 * Show an error message to the user — in the modal info area or as a dialog.
 *
 * @param {string}  message
 * @param {object}  ctx
 * @param {boolean} ctx.isModalOpen
 * @param {jQuery}  ctx.infoTextContainer
 * @param {object}  ctx.dotsState           Mutable
 * @param {function} ctx.showInfoMessage
 */
export function showError(message, ctx) {
    console.error(message);

    if (ctx.isModalOpen && ctx.infoTextContainer && ctx.infoTextContainer.length) {
        if (ctx.dotsState.interval) {
            clearInterval(ctx.dotsState.interval);
            ctx.dotsState.interval = null;
        }
        ctx.showInfoMessage(message);
        return;
    }

    const modal = $('#growtypeChatErrorModal');
    if (modal.length) {
        modal.find('.modal-body').html('<p>' + message + '</p>');
        modal.modal('show');
    } else if (typeof loadAndShowModal === 'function') {
        loadAndShowModal(
            growtype_chat_ajax.get_error_modal_action,
            'growtypeChatErrorModal',
            function ($m) { $m.find('.modal-body').html('<p>' + message + '</p>'); }
        );
    } else {
        alert(message);
    }
}

/**
 * Select and save a matched voice for the character.
 */
export function selectAndSaveMatchedVoice(humeVoice, cartesiaVoice, btnElement, resolveChatSettingOption, updateChatSettings) {
    const voiceToSelect = resolveChatSettingOption('tts_model_used', [humeVoice, cartesiaVoice]);

    if (voiceToSelect) {
        updateChatSettings('tts_model_used', voiceToSelect, true);

        if (btnElement) {
            const btn = $(btnElement);
            const originalText = btn.text();
            btn.text('Voice Matched!').addClass('btn-success').removeClass('btn-secondary');
            setTimeout(() => {
                btn.text(originalText).removeClass('btn-success').addClass('btn-secondary');
            }, 2000);
        }
    }
}

/**
 * Show or hide the match-voice block based on current settings.
 */
export function syncMatchVoiceVisibility(modal) {
    const btn = modal.find('.btn-match-voice');
    if (!btn.length) return;

    const humeVoice = btn.attr('data-voice-hume');
    const cartesiaVoice = btn.attr('data-voice-cartesia');
    const currentVoice = $('.chat-settings select[name="tts_model_used"]').val();

    const isMatched = currentVoice && (currentVoice === humeVoice || currentVoice === cartesiaVoice);
    modal.find('.modal-videocall-match-voice').toggle(!isMatched);
}
